import { useCallback } from 'react';

/**
 * Enhanced number input with styled increment/decrement buttons.
 * Replaces the default browser spinner with a cleaner, theme-matched control.
 */
export default function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
  disabled = false,
  className = '',
  style = {},
  inputStyle = {},
  inputClassName = '',
  ...rest
}) {
  const num = value === '' ? '' : Number(value);
  const canDecrement = min == null || (typeof num === 'number' && num > min);
  const canIncrement = max == null || (typeof num === 'number' && num < max);

  const handleIncrement = useCallback(() => {
    if (disabled) return;
    const next = value === '' ? (min ?? 0) : Math.min(Number(value) + step, max ?? Infinity);
    onChange(String(next));
  }, [value, onChange, min, max, step, disabled]);

  const handleDecrement = useCallback(() => {
    if (disabled) return;
    const next = value === '' ? (max ?? 0) : Math.max(Number(value) - step, min ?? -Infinity);
    onChange(String(next));
  }, [value, onChange, min, max, step, disabled]);

  return (
    <div
      className={`number-input-wrapper ${className}`}
      style={{ position: 'relative', display: 'flex', alignItems: 'stretch', ...style }}
    >
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
        className={`number-input-field ${inputClassName}`}
        style={{ paddingRight: '2.5rem', ...inputStyle }}
        {...rest}
      />
      <div className="number-input-spinner">
        <button
          type="button"
          aria-label="Increase"
          onClick={handleIncrement}
          disabled={disabled || !canIncrement}
          className="number-input-btn number-input-btn-up"
        >
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 5L5 1L9 5" />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Decrease"
          onClick={handleDecrement}
          disabled={disabled || !canDecrement}
          className="number-input-btn number-input-btn-down"
        >
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 1L5 5L1 1" />
          </svg>
        </button>
      </div>
    </div>
  );
}
